<template>
  <main class="badge-detail">
    <div v-if="isApp" class="badge-detail__navigation">
      <button type="button" aria-label="返回" @click="goBack">
        <DuIcon name="arrow-left" size="24px" />
      </button>
    </div>

    <div v-if="!badge" class="badge-detail__status" role="status">
      <template v-if="loading">加载中…</template>
      <template v-else>
        <p>{{ error || "徽章不存在或暂时无法查看" }}</p>
        <DuButton v-if="userBadgeId" size="small" @click="refresh"
          >重试</DuButton
        >
      </template>
    </div>

    <div v-else class="badge-detail__content">
      <BadgeLarge
        :key="userBadgeId"
        :badge-style="badge.badgeStyleType"
        :url="
          badge.badgeStyleType === 'THREE_DIMENSION'
            ? badge.threeDimensionFileUrl
            : badge.attachmentLarge
        "
        :fallback-url="badge.attachmentLarge"
        :name="badge.badgeName"
      />

      <div class="badge-detail__info">
        <div class="badge-detail__heading">
          <h1>{{ badge.badgeName }}</h1>
          <button
            v-if="badge.badgeDescription && badge.url"
            type="button"
            class="badge-detail__description"
            @click="openDescription"
          >
            {{ badge.badgeDescription }}
            <DuIcon name="arrow-right" size="12px" />
          </button>
          <p
            v-else-if="badge.badgeDescription"
            class="badge-detail__description"
          >
            {{ badge.badgeDescription }}
          </p>
        </div>
        <div class="badge-detail__dates">
          <section>
            <p>{{ formatDate(badge.availableAt, badge.availableAtText) }}</p>
            <h2>获得时间</h2>
          </section>
          <section>
            <p>{{ formatDate(badge.disableAt, badge.disableAtText) }}</p>
            <h2>可佩戴</h2>
          </section>
        </div>
      </div>

      <div class="badge-detail__actions">
        <div class="badge-detail__action-row">
          <DuButton v-if="isApp && isMine" size="large" @click="share"
            >分享</DuButton
          >
          <DuButton
            v-if="isMine && !badge.isWearing"
            size="large"
            color="secondary"
            :loading="actionPending"
            :disabled="actionPending"
            @click="wear('WEARING')"
            >佩戴</DuButton
          >
          <DuButton
            v-else-if="isApp && currentUserId && from !== 'badgeWall'"
            size="large"
            color="secondary"
            @click="goToMyBadgeWall"
            >我的徽章墙</DuButton
          >
        </div>
        <div class="badge-detail__remove">
          <DuButton
            v-if="isMine && badge.isWearing"
            color="default"
            size="small"
            type="text"
            :loading="actionPending"
            :disabled="actionPending"
            @click="wear('UN_WEARING')"
            >卸下</DuButton
          >
        </div>
        <button
          v-if="error"
          type="button"
          class="badge-detail__retry"
          @click="refresh"
        >
          {{ error }}，点击重试
        </button>
      </div>
    </div>

    <button
      v-if="!isApp"
      type="button"
      class="badge-detail__open-app"
      @click="openApp"
    >
      临界 App 内打开
    </button>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { DuButton, DuIcon } from "dangoui";
import { showToast } from "vant";
import dayjs from "dayjs";
import jb from "@frontend/effuse";
import BadgeLarge from "@/components/badges/BadgeLarge.vue";
import ApiLogin from "@/apis/login";
import { useBadgeDetail } from "@/hooks/useBadgeDetail";
import { useTitle } from "@/hooks/useTitle";
import { useUsersStore } from "@/store/user";
import { isLinjieApp } from "@/utils/env-related";
import { invokeAppByYYB } from "@/utils/mob";
import sensors from "@/sensors";

const route = useRoute();
const userStore = useUsersStore();
const { setTitle } = useTitle();
const isApp = isLinjieApp();
const appUserId = ref("");
let disposed = false;

const queryString = (value) => {
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === "string" ? first : "";
};
// int64 ID 始终使用字符串，避免精度丢失。
const userBadgeId = computed(() => queryString(route.query.userBadgeId));
const from = computed(() => queryString(route.query.from));
const { badge, loading, error, actionPending, refresh, changeWearing } =
  useBadgeDetail(userBadgeId);
const currentUserId = computed(() => {
  if (isApp) return appUserId.value;
  return userStore.getIsLogin
    ? String(userStore.userInfo?.userId || userStore.userInfo?.id || "")
    : "";
});
const isMine = computed(() =>
  Boolean(currentUserId.value && badge.value?.ownerId === currentUserId.value)
);
const badgePath = computed(
  () =>
    `/badges/detail?${new URLSearchParams({
      userBadgeId: userBadgeId.value,
      isShowNavigationBar: "false",
    })}`
);
const shareUrl = computed(() => {
  const url = new URL(badgePath.value, window.location.origin);
  url.searchParams.set("qds_channel", "share_btn");
  url.searchParams.set(
    "_appName",
    queryString(route.query._appName) || "kuril"
  );
  return url.toString();
});

function formatDate(value, text) {
  if (!value) return text || "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY.MM.DD") : text || "—";
}

function track(event) {
  sensors.track(event, {
    badge_id: badge.value?.badgeId,
    badge_name: badge.value?.badgeName,
  });
}

function goBack() {
  jb.navigateBack();
}

function openDescription() {
  if (!badge.value?.url) return;
  if (isApp) jb.navigateToUrl({ url: badge.value.url });
  else window.location.assign(badge.value.url);
}

async function wear(action) {
  if (!isMine.value) return;
  try {
    await changeWearing(action);
  } catch (err) {
    if (!disposed)
      showToast(err instanceof Error ? err.message : "操作失败，请重试");
  }
}

function share() {
  if (!badge.value) return;
  track("badge_share");
  jb.shareUrl({
    title: "猜猜我达成了什么隐藏成就?",
    content: `刚在临界触发「${badge.value.badgeName}」稀有成就！你也来临界 App 挑战`,
    imageUrl: badge.value.attachmentLarge,
    url: shareUrl.value,
  });
}

function goToMyBadgeWall() {
  track("badgewall");
  jb.navigateTo({
    url: `/pages-common/user-home-page/self/badge-wall?userId=${encodeURIComponent(
      currentUserId.value
    )}`,
  });
}

function openApp() {
  track("entry_daolink_download");
  invokeAppByYYB({
    targetPath: "badges/detail",
    params: { userBadgeId: userBadgeId.value },
  });
}

function receiveAppUser(info) {
  if (disposed) return;
  try {
    const user = typeof info === "string" ? JSON.parse(info || "{}") : info;
    appUserId.value = String(user?.id || user?.userId || "");
  } catch {
    // 未登录或旧版 bridge 未返回用户信息时，仍可公开浏览详情。
  }
}

onMounted(async () => {
  if (!isApp) return;
  jb.getUserInfo({ success: receiveAppUser });
  // 新版容器通过 token 请求用户信息；不将公开详情页标为 needLogin。
  try {
    if (!jb.tokenSync("")) return;
    const res = await ApiLogin.getUserInfo({});
    if (String(res.code) === "0" && res.data?.account)
      receiveAppUser(res.data.account);
  } catch {
    // 用户信息不可用时隐藏个人操作，不影响徽章展示。
  }
});
watch(
  () => badge.value?.badgeName,
  (name) => setTitle(name || "徽章详情"),
  { immediate: true }
);
onBeforeUnmount(() => {
  disposed = true;
});
</script>

<style lang="scss" scoped>
.badge-detail {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  flex-direction: column;
  padding-bottom: max(68rpx, env(safe-area-inset-bottom));
  background: linear-gradient(180deg, #ededf2 0%, #f7f7f9 100%);
  color: var(--du-text-1, #131416);

  button {
    cursor: pointer;
  }
  button:focus-visible {
    outline: 2px solid var(--du-primary-color, #5230d3);
    outline-offset: 3px;
  }

  &__navigation {
    height: 88rpx;
    margin-top: max(118rpx, env(safe-area-inset-top));
    flex: none;
    button {
      display: flex;
      width: 88rpx;
      height: 88rpx;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      color: inherit;
    }
  }

  &__status {
    display: flex;
    min-height: 720rpx;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24rpx;
    padding: 40rpx;
    font-size: 28rpx;
    text-align: center;
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 48rpx;
    margin-top: 40rpx;
    padding: 0 20rpx;
  }
  &__heading {
    display: flex;
    flex-direction: column;
    gap: 16rpx;
  }
  h1 {
    margin: 0;
    font-size: 48rpx;
    font-weight: 500;
    line-height: 60rpx;
    text-align: center;
    overflow-wrap: anywhere;
  }

  &__description {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    padding: 0;
    background: transparent;
    color: var(--du-text-3, #8d8e91);
    font-size: 24rpx;
    font-weight: 500;
    line-height: 36rpx;
    text-align: center;
    overflow-wrap: anywhere;
  }

  &__dates {
    display: flex;
    section {
      display: flex;
      flex: 1;
      flex-direction: column;
      align-items: center;
      gap: 8rpx;
    }
    p {
      font-size: 28rpx;
      font-weight: 500;
      line-height: 44rpx;
    }
    h2 {
      color: var(--du-text-3, #8d8e91);
      font-size: 20rpx;
      font-weight: 400;
      line-height: 22rpx;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 16rpx;
    margin-top: 114rpx;
    padding: 0 20rpx;
  }
  &__action-row {
    display: flex;
    gap: 16rpx;
    > * {
      min-width: 0;
      flex: 1;
    }
  }
  &__remove {
    display: flex;
    height: 60rpx;
    justify-content: center;
  }
  &__retry {
    border: 0;
    background: transparent;
    color: inherit;
    font-size: 24rpx;
  }

  &__open-app {
    position: fixed;
    z-index: 10;
    bottom: calc(128rpx + env(safe-area-inset-bottom));
    left: 50%;
    display: flex;
    height: 80rpx;
    align-items: center;
    justify-content: center;
    padding: 0 32rpx;
    border: 0;
    border-radius: 16rpx;
    background: linear-gradient(151deg, #2b1ac0 0%, #7247dc 100%);
    box-shadow: 0 2rpx 40rpx rgba(12, 12, 12, 0.08);
    color: #fff;
    font-size: 28rpx;
    font-weight: 500;
    white-space: nowrap;
    transform: translateX(-50%);
  }
}
</style>
